/**
 * Price modifier fields we read from a component group for pricing.
 * @typedef {{ priceModifierType: string | null | undefined, priceModifierValue: number | string | null | undefined }} PriceModifiers
 */

/**
 * Returns the component group's price modifiers for a variant, preferring the group
 * the user selected in this slot when the same variant appears in multiple groups.
 *
 * @param {Map<string, { productVariantIds?: string[], priceModifierType?: string, priceModifierValue?: number | string }>} componentGroups
 * @param {string} variantId
 * @param {string | null | undefined} preferredGroupId - Group id from the slot selection (card context)
 * @returns {PriceModifiers}
 */
export function getPriceModifiersForVariant(componentGroups, variantId, preferredGroupId) {
  if (preferredGroupId) {
    const preferred = componentGroups.get(preferredGroupId);
    if (preferred?.productVariantIds?.includes(variantId)) {
      return {
        priceModifierType: preferred.priceModifierType ?? null,
        priceModifierValue: preferred.priceModifierValue ?? null
      };
    }
  }

  for (const [, componentGroup] of componentGroups.entries()) {
    if (componentGroup.productVariantIds?.includes(variantId)) {
      return {
        priceModifierType: componentGroup.priceModifierType ?? null,
        priceModifierValue: componentGroup.priceModifierValue ?? null
      };
    }
  }

  return { priceModifierType: null, priceModifierValue: null };
}

/**
 * Resolves which component group id to persist on a slot selection.
 *
 * @param {Object} options
 * @param {Map<string, { productVariantIds?: string[] }>} options.componentGroups
 * @param {string | null | undefined} options.explicitGroupId - From the card the user clicked
 * @param {string | string[] | null | undefined} options.itemSelected
 * @param {string | null | undefined} options.variantFallbackGroupId - From hydrated variant (may be ambiguous)
 * @returns {string | null}
 */
export function resolveSelectionComponentGroupId({
  componentGroups,
  explicitGroupId,
  itemSelected,
  variantFallbackGroupId
}) {
  if (itemSelected === null || itemSelected === undefined) {
    return null;
  }

  if (Array.isArray(itemSelected)) {
    if (itemSelected.length === 0) {
      return null;
    }
    if (explicitGroupId) {
      const group = componentGroups.get(explicitGroupId);
      if (group && itemSelected.every((vid) => group.productVariantIds?.includes(vid))) {
        return explicitGroupId;
      }
    }
    return variantFallbackGroupId ?? null;
  }

  if (explicitGroupId && itemSelected) {
    const group = componentGroups.get(explicitGroupId);
    if (group?.productVariantIds?.includes(itemSelected)) {
      return explicitGroupId;
    }
  }

  return variantFallbackGroupId ?? null;
}
